import type * as Api from 'src/api'
import { type TranslationKeys } from 'src/translations'

export const DEFAULT_PAGE = '1'
export const DEFAULT_PER_PAGE = '20'
export const DEFAULT_TYPE: Api.AdministratorGraduatesStatisticsType = 'willQualifyList'
/** "Active". Without a status filter the request covers every student and takes about a minute. */
export const DEFAULT_EDU_STATUSES: readonly string[] = ['1']

export const GRADUATES_TYPES: ReadonlyArray<{
  readonly value: Api.AdministratorGraduatesStatisticsType
  readonly label: TranslationKeys
  readonly description: TranslationKeys
}> = [
  {
    value: 'willQualifyCourses',
    label: 'statistics:will_qualify_courses',
    description: 'statistics:will_qualify_courses_description',
  },
  {
    value: 'willQualifyList',
    label: 'statistics:will_qualify_list',
    description: 'statistics:will_qualify_list_description',
  },
  {
    value: 'qualifiedCourses',
    label: 'statistics:qualified_courses',
    description: 'statistics:qualified_courses_description',
  },
  {
    value: 'qualifiedList',
    label: 'statistics:qualified_list',
    description: 'statistics:qualified_list_description',
  },
]

/** Filter values as they live in the URL and in the filter form. Empty string means "not set". */
export interface GraduatesFormValues {
  readonly type: Api.AdministratorGraduatesStatisticsType
  readonly semId: string
  readonly q: string
  readonly degree: string
  readonly facultyId: string
  readonly minorFacultyId: string
  readonly eduStatuses: readonly string[]
  readonly schoolId: string
  readonly programId: string
}

export type GraduatesFilters = Api.postAdministratorStatisticsGraduatesExportParams['body']

const SCALAR_KEYS = ['semId', 'q', 'degree', 'facultyId', 'minorFacultyId', 'schoolId', 'programId'] as const

function isGraduatesType(value: string | null): value is Api.AdministratorGraduatesStatisticsType {
  return GRADUATES_TYPES.some((type) => type.value === value)
}

export function isCoursesType(type: Api.AdministratorGraduatesStatisticsType): boolean {
  return type === 'willQualifyCourses' || type === 'qualifiedCourses'
}

/**
 * A URL without `type` has never been searched, so it gets the defaults.
 * After a search `type` is always present, and a missing `eduStatuses` means the user cleared the statuses.
 */
export function parseFormValues(searchParams: URLSearchParams): GraduatesFormValues {
  const type = searchParams.get('type')
  const isFresh = type == null

  return {
    type: isGraduatesType(type) ? type : DEFAULT_TYPE,
    semId: searchParams.get('semId') ?? '',
    q: searchParams.get('q') ?? '',
    degree: searchParams.get('degree') ?? '',
    facultyId: searchParams.get('facultyId') ?? '',
    minorFacultyId: searchParams.get('minorFacultyId') ?? '',
    eduStatuses: isFresh ? DEFAULT_EDU_STATUSES : searchParams.getAll('eduStatuses'),
    schoolId: searchParams.get('schoolId') ?? '',
    programId: searchParams.get('programId') ?? '',
  }
}

export function formValuesToSearchParams(values: GraduatesFormValues, perPage: string): URLSearchParams {
  const params = new URLSearchParams({ type: values.type })
  SCALAR_KEYS.forEach((key) => {
    const value = values[key].trim()
    if (value !== '') {
      params.set(key, value)
    }
  })
  values.eduStatuses.forEach((status) => params.append('eduStatuses', status))
  params.set('page', DEFAULT_PAGE)
  params.set('perPage', perPage)

  return params
}

function toNumber(value: string): number | undefined {
  const parsed = parseInt(value, 10)
  return Number.isFinite(parsed) ? parsed : undefined
}

/** Shared by the list and the Excel export. Empty values are left out, as the backend expects. */
export function formValuesToFilters(values: GraduatesFormValues): GraduatesFilters {
  const q = values.q.trim()
  const eduStatuses = values.eduStatuses.map(toNumber).filter((status): status is number => status != null)

  return {
    type: values.type,
    ...(toNumber(values.semId) != null && { semId: toNumber(values.semId) }),
    ...(q !== '' && { q }),
    ...(toNumber(values.degree) != null && { degree: toNumber(values.degree) }),
    ...(toNumber(values.facultyId) != null && { facultyId: toNumber(values.facultyId) }),
    ...(toNumber(values.minorFacultyId) != null && { minorFacultyId: toNumber(values.minorFacultyId) }),
    ...(eduStatuses.length > 0 && { eduStatuses }),
    ...(toNumber(values.schoolId) != null && { schoolId: toNumber(values.schoolId) }),
    ...(toNumber(values.programId) != null && { programId: toNumber(values.programId) }),
  }
}

/** Fields whose object form (`{ id|value, name }` or `{ key, title }`) collapses to one cell. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

const HIDDEN_KEYS = new Set(['id', 'userId', 'profileId'])

function toText(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

/**
 * Flattens a row into cells in field order, matching `meta.headers` (which are labels only).
 * Value objects show their `name` (or `title`); grouping objects such as `student` are expanded in place.
 * Internal ids are skipped.
 */
export function rowToCells(row: Record<string, unknown>): string[] {
  return Object.entries(row).flatMap(([key, value]) => {
    if (HIDDEN_KEYS.has(key)) return []
    if (isRecord(value)) {
      if ('name' in value) return [toText(value.name)]
      if ('title' in value) return [toText(value.title)]
      return rowToCells(value)
    }
    return [toText(value)]
  })
}

/** Backend messages arrive translated; they are shown as they are. */
export function errorToMessages(error: unknown): readonly string[] {
  if (!isRecord(error)) return []
  if (error.type === 'ErrorsObject' && isRecord(error.errors)) {
    return Object.values(error.errors).flatMap((messages) =>
      Array.isArray(messages) ? messages.filter((message): message is string => typeof message === 'string') : []
    )
  }
  if (typeof error.message === 'string' && error.message !== '') {
    return [error.message]
  }
  return []
}
